import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { makeStyles } from 'tss-react/mui';
import { InputAdornment, TextField, Select, MenuItem, Button, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

import { healthBenefitPaymentRequestState } from '../../atoms/HealthBenefitState';

const useStyles = makeStyles()((theme) => ({
  searchForm: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  searchInput: {
    flexGrow: 2,
    backgroundColor: '#fff',
    '&>.MuiInputBase-root': {
      marginLeft: 20,
      '&>.MuiOutlinedInput-input': {
        paddingTop: '15px',
        paddingBottom: '15px',
      },
    },
  },
  searchBtn: {
    width: 200,
    height: 53,
    marginLeft: 20,
  },
  closeBtn: {
    padding: 8,
  },
  select: {
    width: 180,
  },
}));

const STATUS_OPTIONS = [
  { value: '', label: 'All status' },
  { value: 'SUBMITTED', label: 'SUBMITTED' },
  { value: 'APPROVED', label: 'APPROVED' },
  { value: 'REJECTED', label: 'REJECTED' },
  { value: 'UNDER_REVIEW', label: 'UNDER_REVIEW' },
];

// Generate year options from current year to 2021 (descending order)
const genYearOptions = () => {
  const currentYear = moment().year();
  const years = [];
  for (let year = currentYear; year >= 2021; year--) {
    years.push(year);
  }
  return years;
};

const YEAR_OPTIONS = genYearOptions();
const QUARTER_OPTIONS = [4, 3, 2, 1];

const CURRENT_YEAR = moment().year();
const CURRENT_QUARTER = moment().quarter();

// Calculate previous quarter
const previousQuarterMoment = moment().subtract(1, 'quarter');
const PREVIOUS_QUARTER = previousQuarterMoment.quarter();
const PREVIOUS_QUARTER_YEAR = previousQuarterMoment.year();

const SearchForm = ({ initValue, placeholder }) => {
  const [healthBenefits, setHealthBenefits] = useRecoilState(healthBenefitPaymentRequestState);
  const { classes } = useStyles();
  const [keyword, setKeyword] = useState(initValue);

  const handleSearch = (e) => {
    e.preventDefault();
    setHealthBenefits({ ...healthBenefits, search_text: keyword, page: 1 });
    return false;
  };

  const handleChangeSearch = (event) => {
    setKeyword(event.target.value);
  };

  const handleResetSearch = (e) => {
    setKeyword('');
    setHealthBenefits({ ...healthBenefits, search_text: null });
    return false;
  };

  const handleChangeSelect = (event) => {
    setHealthBenefits({
      ...healthBenefits,
      status: event.target.value,
      page: 1,
    });
  };

  const handleChangeYear = (event) => {
    const {
      target: { value },
    } = event;

    let selectedYears = typeof value === 'string' ? value.split(',') : value;

    // Handle "ALL" selection
    if (selectedYears.includes('ALL')) {
      if (healthBenefits.years?.includes('ALL')) {
        // If ALL was already selected, deselect it
        selectedYears = selectedYears.filter((y) => y !== 'ALL');
      } else {
        // Select ALL
        selectedYears = ['ALL'];
      }
    }

    setHealthBenefits((previous) => ({
      ...previous,
      years: selectedYears,
      page: 1,
    }));
  };

  const handleChangeQuarter = (event) => {
    const {
      target: { value },
    } = event;

    let selectedQuarters = typeof value === 'string' ? value.split(',') : value;

    // Handle "ALL" selection
    if (selectedQuarters.includes('ALL')) {
      if (healthBenefits.quarters?.includes('ALL')) {
        // If ALL was already selected, deselect it
        selectedQuarters = selectedQuarters.filter((q) => q !== 'ALL');
      } else {
        // Select ALL
        selectedQuarters = ['ALL'];
      }
    }

    setHealthBenefits((previous) => ({
      ...previous,
      quarters: selectedQuarters,
      page: 1,
    }));
  };

  const handleCloseYearSelect = () => {
    // If no year is selected, set to current year
    if (!healthBenefits.years || healthBenefits.years.length === 0) {
      setHealthBenefits((previous) => ({
        ...previous,
        years: [CURRENT_YEAR],
        page: 1,
      }));
    }
  };

  const handleCloseQuarterSelect = () => {
    // If no quarter is selected, set to previous quarter
    if (!healthBenefits.quarters || healthBenefits.quarters.length === 0) {
      setHealthBenefits((previous) => ({
        ...previous,
        quarters: [PREVIOUS_QUARTER],
        page: 1,
      }));
    }
  };

  useEffect(() => {
    // Set default to previous quarter
    setHealthBenefits((previous) => ({
      ...previous,
      years: [PREVIOUS_QUARTER_YEAR],
      quarters: [PREVIOUS_QUARTER],
    }));
  }, []);

  const renderYearValue = (selected) => {
    if (selected.includes('ALL')) {
      return 'ALL Years';
    }
    return selected.join(', ');
  };

  const renderQuarterValue = (selected) => {
    if (selected.includes('ALL')) {
      return 'ALL Quarters';
    }
    return selected.map((q) => `Q${q}`).join(', ');
  };

  const isLoading = healthBenefits.isListing;

  return (
    <form className={classes.searchForm} noValidate autoComplete="off" onSubmit={handleSearch}>
      <Select variant="outlined" displayEmpty value={healthBenefits.status} onChange={handleChangeSelect} className={classes.select} disabled={isLoading}>
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        multiple
        value={healthBenefits.years || []}
        onChange={handleChangeYear}
        onClose={handleCloseYearSelect}
        className={classes.select}
        style={{ marginLeft: '20px' }}
        input={<OutlinedInput />}
        renderValue={renderYearValue}
        displayEmpty
        disabled={isLoading}
      >
        <MenuItem value="ALL">
          <Checkbox checked={healthBenefits.years?.includes('ALL')} />
          <ListItemText primary="ALL" />
        </MenuItem>
        {YEAR_OPTIONS.map((year) => (
          <MenuItem key={year} value={year} disabled={healthBenefits.years?.includes('ALL')}>
            <Checkbox checked={healthBenefits.years?.indexOf(year) > -1} />
            <ListItemText primary={year} />
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        multiple
        value={healthBenefits.quarters || []}
        onChange={handleChangeQuarter}
        onClose={handleCloseQuarterSelect}
        className={classes.select}
        style={{ marginLeft: '20px' }}
        input={<OutlinedInput />}
        renderValue={renderQuarterValue}
        displayEmpty
        disabled={isLoading}
      >
        <MenuItem value="ALL">
          <Checkbox checked={healthBenefits.quarters?.includes('ALL')} />
          <ListItemText primary="ALL" />
        </MenuItem>
        {QUARTER_OPTIONS.map((quarter) => (
          <MenuItem key={quarter} value={quarter} disabled={healthBenefits.quarters?.includes('ALL')}>
            <Checkbox checked={healthBenefits.quarters?.indexOf(quarter) > -1} />
            <ListItemText primary={`Quarter ${quarter}`} />
          </MenuItem>
        ))}
      </Select>
      <TextField
        onChange={handleChangeSearch}
        size="medium"
        value={keyword}
        variant="outlined"
        id="standard-start-adornment"
        className={classes.searchInput}
        placeholder={placeholder}
        disabled={isLoading}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment className={classes.searchIcon} position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: keyword && (
              <IconButton className={classes.closeBtn} onClick={() => handleResetSearch()} size="large" disabled={isLoading}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            ),
          },
        }}
      />
      <Button type="submit" variant="contained" size="large" disableElevation className={classes.searchBtn} disabled={isLoading}>
        {isLoading ? 'Searching...' : 'Search'}
      </Button>
    </form>
  );
};
export default React.memo(SearchForm);
